import {
  useGetAssembliesForEvaluationQuery,
  useStructureDefectLegendsQuery,
} from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import useLiveSyncAssemblyPois from '../../shared/hooks/useLiveSyncAssemblyPois';

export const useBulkAssemblyData = (params: {
  assemblyIds: string[];
  enableLiveSync?: boolean;
}) => {
  const assemblyTemplate = useAtomValue(state.assemblyTemplate);
  const setComponentDetailsById = useSetAtom(state.componentDetailsById);
  const setComponentAreasById = useSetAtom(state.componentAreasById);
  const structureId = useGetStructureId();

  const setDefectsLegends = useSetAtom(state.componentsDefectLegends);
  const setComponentsDetailsLoading = useSetAtom(state.componentsDetailsLoading);

  const enableLiveSync = params.enableLiveSync !== false;
  const assemblyIds = params?.assemblyIds ?? [];

  const processAssemblyData = useCallback(
    (
      assemblies: Array<{
        id: string;
        templateFieldData?: Record<string, string>;
        area?: number | null;
      }>
    ) => {
      const detailsById = assemblies.reduce(
        (acc, assembly) => {
          if (assembly.templateFieldData) {
            acc[assembly.id] = assembly.templateFieldData;
          }
          return acc;
        },
        {} as { [key: string]: Record<string, string> }
      );
      const areasById = assemblies.reduce(
        (acc, assembly) => {
          acc[assembly.id] = assembly.area ?? 0;
          return acc;
        },
        {} as { [key: string]: number }
      );
      setComponentDetailsById(detailsById);
      setComponentAreasById(areasById);
    },
    [setComponentDetailsById, setComponentAreasById]
  );

  useStructureDefectLegendsQuery({
    variables: { structureId },
    onCompleted: (data) => {
      setDefectsLegends(data?.structure?.defectLegends || []);
    },
  });

  const { loading: assemblyDataLoading, refetch } = useGetAssembliesForEvaluationQuery({
    variables: {
      input: {
        structureId,
        templateId: assemblyTemplate?.id ?? '',
        assemblyIds,
      },
    },
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data?.getAssembliesByTemplateFieldsFilters) {
        processAssemblyData(data.getAssembliesByTemplateFieldsFilters);
      }
    },
    onError: (error) => {
      console.error(`Error fetching assemblies: ${error.message}`);
    },
    skip: !assemblyTemplate || assemblyIds.length === 0,
  });

  useLiveSyncAssemblyPois(
    enableLiveSync
      ? () => {
          refetch().then((result) => {
            if (result.data?.getAssembliesByTemplateFieldsFilters) {
              processAssemblyData(result.data.getAssembliesByTemplateFieldsFilters);
            }
          });
        }
      : undefined,
    enableLiveSync
  );

  useEffect(() => {
    setComponentsDetailsLoading(assemblyDataLoading);
  }, [assemblyDataLoading, setComponentsDetailsLoading]);
};
